import { Col, Row } from '@advanza/grid'
import { InputField, SelectSimple, Toggle } from '@advanza/input'
import Modal from '@advanza/modal'
import { Button, Divider, Ellipsis, Flag, HoverText, Icon, PreIcon } from '@advanza/ui'
import NiceModal from '@ebay/nice-modal-react'
import {
    CANCEL_MODE_SILENT,
    fetchProvider,
    saveApi,
    saveLeadSetting,
    unsubscribe,
} from 'actions/providers'
import AutoRow from 'components/misc/AutoRow'
import PremiumCancelModal from 'components/providers/PremiumCancelModal'
import EntityComponent from 'components/services/EntityComponent'
import EntityComponentContainer from 'containers/EntityComponentContainer'
import { getDate } from 'date'
import format from 'date-fns/format'
import { useReplacementLeadStats } from 'hooks/providerHooks'
import LeadSettingsData from 'interfaces/LeadSettings/LeadSettings'
import {
    DEFAULT_MAX_WEEK_PER_SERVICE,
    LeadServiceSettings,
    Subscription,
    WEEKLY_LIMIT_PER_ACCOUNT,
    WEEKLY_LIMIT_PER_SERVICE,
} from 'interfaces/LeadSettings/LeadSettingTypes'
import { ReplacementLeadStats } from 'interfaces/ReplacementLeads'
import LoadingDotsTeam from 'misc/LoadingDotsTeam'
import { isSuperUser, hasAccess as userHasAccess } from 'misc/user'
import { Fragment } from 'react'
import LimitReachedLabel from './LimitReachedLabel'
import ReplacementLeadBadge from './ReplacementLeadBadge'

interface LeadSettingsProps {
    entity: LeadSettingsData
    entities?: Record<string, any>
    provider: {
        logs?: string[]
        isFreeTrial?: boolean
        link_exchange_widget?: {
            active?: boolean
        }
        phone_routing_nr?: string
        active_request_sending_apis?: Api[]
    }
    logs: Record<string, Log>
    saveApi: (apiId: string) => void
    unsubscribe: (providerId: number, mode: string, reason: string | null) => void
    /** Null while still fetching; the badges fall back to 0 until it arrives. */
    replacementLeadStats: ReplacementLeadStats | null
}

interface Api {
    api_id: string
    display_name: string
    always_mail_lead: boolean
}

// The partner push lead mail is configured per subscription on the Partner push
// page; the toggle below would change apis.always_mail_lead for ALL partner
// push accounts at once, so it is hidden for this api.
const PARTNER_PUSH_API_ID = 19

interface Log {
    log_id: number
    log_type: string
    created: string
}

class LeadSettings extends EntityComponent {
    constructor(props: LeadSettingsProps) {
        super(props)
        this.state = {
            warningMsg: null,
            editingValues: {} as Record<number, string>,
        }
    }

    editFields = () => {
        const entity = this.getEntity()
        return {
            max_day: {
                type: 'number',
                msg: 'D',
                before: entity.max_day === 0 && <Icon name="all_inclusive" />,
                validator: (val: number) => val === 0 || val < 999,
                inputStyle: {
                    minWidth: 50,
                    color: entity.max_day === 0 ? 'grey' : undefined,
                },
            },
            max_week: {
                type: 'number',
                msg: 'W',
                before: entity.max_week === 0 && <Icon name="all_inclusive" />,
                validator: (val: number) => val === 0 || val < 999,
                inputStyle: {
                    minWidth: 50,
                    color: entity.max_week === 0 ? 'grey' : undefined,
                },
            },
            max_month: {
                type: 'number',
                msg: 'M',
                before: entity.max_month === 0 && <Icon name="all_inclusive" />,
                validator: (val: number) => val === 0 || val < 999,
                inputStyle: {
                    minWidth: 50,
                    color: entity.max_month === 0 ? 'grey' : undefined,
                },
            },
            website_clicks_max_week: {
                type: 'number',
                msg: 'Website ',
                before: entity.website_clicks_max_week === 0 && <Icon name="all_inclusive" />,
                validator: (val: number) => val === 0 || val < 999,
                inputStyle: {
                    minWidth: 50,
                    color: entity.website_clicks_max_week === 0 ? 'grey' : undefined,
                },
            },
            charge_website_clicks: {
                type: 'boolean',
                msg: <Ellipsis>charge website clicks</Ellipsis>,
            },
            charge_phone_calls: {
                type: 'boolean',
                msg: <Ellipsis>charge phone calls</Ellipsis>,
            },
            is_reclaiming_enabled: {
                type: 'boolean',
                msg: <Ellipsis>reclaiming enabled</Ellipsis>,
            },
            extended_match_details: {
                type: 'boolean',
                msg: (
                    <HoverText enableClick trigger={<Icon fontSize={18} name="contact_mail" />}>
                        Show real email and full phonenumber in lead mails
                    </HoverText>
                ),
            },
        }
    }

    calculateTotalWeekLimit = (serviceSettings: LeadServiceSettings[]): number => {
        const subscriptions = Object.values(
            (this.props.provider.subscriptions ?? {}) as Record<string, Subscription>,
        )
        const inactiveSubscriptionIds = new Set(
            subscriptions.filter((s) => !s.active).map((s) => s.subscription_id),
        )
        const activeSettings = serviceSettings.filter(
            (s) => !inactiveSubscriptionIds.has(s.subscription_id),
        )
        return activeSettings.reduce((sum, setting) => sum + (setting.max_week || 0), 0)
    }

    getServiceSettingForSubscription = (
        subscription: Subscription,
        serviceSettings: LeadServiceSettings[],
    ): LeadServiceSettings | undefined => {
        const bySubscription = serviceSettings.find(
            (setting: LeadServiceSettings) =>
                setting.subscription_id === subscription.subscription_id,
        )

        if (bySubscription) {
            return bySubscription
        }

        return serviceSettings.find(
            (setting: LeadServiceSettings) => setting.service_id === subscription.service_id,
        )
    }

    changeLeadSettingType = (type: string) => {
        const entity = this.props.entity || {}
        const subscriptions = this.props.provider.subscriptions

        if (type === WEEKLY_LIMIT_PER_SERVICE) {
            const existingServiceSettings = entity.service_settings || []

            if (existingServiceSettings.length === 0) {
                const newServiceSettings = (
                    Object.values(subscriptions) as LeadServiceSettings[]
                ).map((service) => ({
                    service_id: service.service_id,
                    subscription_id: service.subscription_id,
                    service_provider_id: entity.service_provider_id,
                    max_week: DEFAULT_MAX_WEEK_PER_SERVICE,
                }))

                const totalWeekLimit = this.calculateTotalWeekLimit(newServiceSettings)

                this.onChangeEntity({
                    lead_settings_type: type,
                    service_settings: newServiceSettings,
                    max_week: totalWeekLimit,
                })
            } else {
                const totalWeekLimit = this.calculateTotalWeekLimit(existingServiceSettings)

                this.onChangeEntity({
                    lead_settings_type: type,
                    service_settings: existingServiceSettings,
                    max_week: totalWeekLimit,
                })
            }
        } else if (
            type === WEEKLY_LIMIT_PER_ACCOUNT &&
            entity.lead_settings_type === WEEKLY_LIMIT_PER_SERVICE
        ) {
            // Preserve service settings when switching to per-account mode
            this.onChangeEntity({
                lead_settings_type: type,
            })
        } else {
            this.onChangeEntity({ lead_settings_type: type })
        }
    }

    changeServiceMaxWeek = (subscriptionId: number, newValue: number) => {
        const entity = this.getEntity()
        const subscription = Object.values(
            this.props.provider.subscriptions as Record<string, Subscription>,
        ).find((sub: Subscription) => sub.subscription_id === subscriptionId)

        const serviceId = subscription?.service_id || 0
        const newSetting = {
            subscription_id: subscriptionId,
            max_week: newValue,
            service_id: serviceId,
            service_provider_id: entity.service_provider_id,
        }

        let updatedSettings: LeadServiceSettings[]

        if (!entity.service_settings || entity.service_settings.length === 0) {
            updatedSettings = [newSetting]
        } else {
            const existingIndex = entity.service_settings.findIndex(
                (setting: LeadServiceSettings) => setting.subscription_id === subscriptionId,
            )

            if (existingIndex >= 0) {
                updatedSettings = [...entity.service_settings]
                updatedSettings[existingIndex] = {
                    ...updatedSettings[existingIndex],
                    max_week: newValue,
                }
            } else {
                const serviceIndex = entity.service_settings.findIndex(
                    (setting: LeadServiceSettings) => setting.service_id === serviceId,
                )

                if (serviceIndex >= 0) {
                    updatedSettings = [...entity.service_settings]
                    updatedSettings[serviceIndex] = {
                        ...updatedSettings[serviceIndex],
                        subscription_id: subscriptionId,
                        service_id: serviceId,
                        max_week: newValue,
                    }
                } else {
                    updatedSettings = [...entity.service_settings, newSetting]
                }
            }
        }

        const totalWeekLimit = this.calculateTotalWeekLimit(updatedSettings)

        this.onChangeEntity({
            service_settings: updatedSettings,
            max_week: totalWeekLimit,
        })
    }

    render() {
        const {
            entity = {} as LeadSettingsData,
            provider = {},
            logs = {},
            saveApi,
            unsubscribe,
        } = this.props
        const { _saving, _isTouched } = entity
        const canToggleReclaiming = isSuperUser() || userHasAccess('admin', 'w')

        // Auto-switch non-premium users from "Per Service" to "Per Account"
        if (!entity.signing_date && entity.lead_settings_type === WEEKLY_LIMIT_PER_SERVICE) {
            this.onChangeEntity({
                lead_settings_type: WEEKLY_LIMIT_PER_ACCOUNT,
                service_settings: [],
            })
        }

        const logArr = (provider.logs || [])
            .map((logId: string) => logs[logId])
            .filter((log: Log | undefined): log is Log => !!log)
            .filter((log: Log) => log.log_type === 'subscribe' || log.log_type === 'unsubscribe')
            .sort((logA: Log, logB: Log) => logA.log_id - logB.log_id)
        const hasFreeWebsiteClicks =
            entity.charge_website_clicks &&
            provider.link_exchange_widget &&
            provider.link_exchange_widget.active
        const phoneRoutingNrRequested = provider.phone_routing_nr?.includes('REQUESTED')
        const onSave = () =>
            this.save().then(({ warningMsg }: { warningMsg?: string }) => {
                if (warningMsg) {
                    this.setState({ warningMsg })
                }
            })
        const subscriptions = (provider.subscriptions ?? {}) as Record<string, Subscription>

        return (
            <div>
                <Modal
                    maxWidth={750}
                    open={!!this.state.warningMsg}
                    close={() => this.setState({ warningMsg: null })}>
                    <h1>Warning!</h1>
                    <p>{this.state.warningMsg}</p>
                    <LoadingDotsTeam
                        imgSrc={'https://static.trustoo.nl/team/white/Pieter.jpg'}
                        size={230}
                    />
                </Modal>
                <AutoRow noWrap>
                    {this.renderInput('max_day')}
                    {this.renderInput('max_week')}
                    {entity.lead_settings_type === WEEKLY_LIMIT_PER_ACCOUNT && (
                        <ReplacementLeadBadge
                            given={this.props.replacementLeadStats?.totals.given ?? 0}
                        />
                    )}
                    {this.renderInput('max_month')}
                    {this.renderInput('website_clicks_max_week')}
                    <AutoRow>
                        <LimitReachedLabel leadSetting={entity} />
                    </AutoRow>
                </AutoRow>
                <Divider m />

                {provider.isFreeTrial && (
                    <>
                        <Divider s />
                        <small>
                            <i>
                                During the free trial period, the day and month limits are
                                overruled. The day limit is always 1 and there is no month limit,
                                but a free trial lead limit.
                            </i>
                        </small>
                    </>
                )}
                <Divider s />
                <SelectSimple
                    options={[
                        { value: WEEKLY_LIMIT_PER_ACCOUNT, name: 'Per Account' },
                        ...(entity.signing_date
                            ? [{ value: WEEKLY_LIMIT_PER_SERVICE, name: 'Per Service' }]
                            : []),
                    ]}
                    value={entity.lead_settings_type}
                    onChange={(e) => this.changeLeadSettingType(e.target.value as string)}
                    placeholder="Weekly Limit Type"
                    legend
                />
                {!entity.signing_date && (
                    <div className="mt-2 text-md text-neutral-600">
                        <i>
                            Per Service limits are only available for premium users. Please sign the
                            agreement first.
                        </i>
                    </div>
                )}
                {entity.lead_settings_type === WEEKLY_LIMIT_PER_SERVICE && (
                    <div className="flex flex-col gap-2 mt-4">
                        {Object.values(subscriptions).map((service) => {
                            const serviceSetting = entity.service_settings
                                ? this.getServiceSettingForSubscription(
                                      service,
                                      entity.service_settings,
                                  )
                                : undefined
                            return (
                                <div
                                    key={service.subscription_id}
                                    className="flex items-center gap-2">
                                    <div>
                                        <a
                                            className="text-bodyDark"
                                            href={`/service/providers/subscription/${service.subscription_id}`}>
                                            #{service.subscription_id}
                                        </a>
                                    </div>
                                    <div className="ml-2">
                                        <Flag countryCode={service.country_code} />
                                    </div>
                                    <div className="flex-1 flex items-center">
                                        <span>{service.serviceName}</span>
                                        {serviceSetting?.week_limit_reached && (
                                            <span className="ml-2 text-sm text-gold inline-flex items-center gap-1">
                                                <Icon
                                                    name="warning"
                                                    classNameIcon="!text-gold"
                                                    size={14}
                                                />
                                                Limit reached
                                            </span>
                                        )}
                                    </div>
                                    <div className="w-32 flex justify-end items-center">
                                        {!service.active && (
                                            <HoverText
                                                trigger={
                                                    <Icon
                                                        name="pause_circle"
                                                        classNameIcon="!text-paused-orange mr-2"
                                                        size={18}
                                                    />
                                                }>
                                                Inactive
                                            </HoverText>
                                        )}
                                        <InputField
                                            type="number"
                                            value={
                                                this.state.editingValues[service.subscription_id] ??
                                                serviceSetting?.max_week ??
                                                DEFAULT_MAX_WEEK_PER_SERVICE
                                            }
                                            placeholder="W"
                                            before={
                                                serviceSetting?.max_week === 0 && (
                                                    <Icon name="all_inclusive" />
                                                )
                                            }
                                            onChange={(e) => {
                                                const inputValue = e.target.value
                                                this.setState((prevState: any) => ({
                                                    editingValues: {
                                                        ...prevState.editingValues,
                                                        [service.subscription_id]: inputValue,
                                                    },
                                                }))
                                            }}
                                            onBlur={(e) => {
                                                const inputValue = e.target.value.trim()
                                                let newValue: number

                                                if (inputValue === '') {
                                                    newValue = 0
                                                } else {
                                                    const parsed = parseInt(inputValue, 10)
                                                    newValue = isNaN(parsed)
                                                        ? serviceSetting?.max_week || 0
                                                        : parsed
                                                }

                                                this.changeServiceMaxWeek(
                                                    service.subscription_id,
                                                    newValue,
                                                )
                                                this.setState((prevState: any) => {
                                                    const newEditingValues = {
                                                        ...prevState.editingValues,
                                                    }
                                                    delete newEditingValues[service.subscription_id]
                                                    return { editingValues: newEditingValues }
                                                })
                                            }}
                                            style={{
                                                minWidth: 115,
                                                maxWidth: 115,
                                                height: 36,
                                                color:
                                                    serviceSetting?.max_week === 0
                                                        ? 'grey'
                                                        : undefined,
                                            }}
                                        />
                                    </div>
                                    <div className="w-12 flex justify-end">
                                        <ReplacementLeadBadge
                                            given={
                                                this.props.replacementLeadStats?.subscriptions.find(
                                                    (s: any) =>
                                                        s.subscriptionId ===
                                                        service.subscription_id,
                                                )?.given ?? 0
                                            }
                                        />
                                    </div>
                                </div>
                            )
                        })}
                        <div className="mt-2 text-md text-neutral-600 italic">
                            <i>
                                Note: The main weekly limit automatically reflects the sum of all
                                service limits.
                            </i>
                        </div>
                    </div>
                )}
                <Divider m />
                <AutoRow>
                    {!entity.signing_date && !entity.cancel_signing_date && (
                        <Button
                            name="text"
                            onClick={() =>
                                this.onChangeEntity({
                                    signing_date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
                                    cancel_signing_date: null,
                                    unsubscribed: null,
                                })
                            }>
                            Agreement signed
                        </Button>
                    )}
                    {entity.cancel_signing_date && (
                        <Button
                            name="text"
                            onClick={() =>
                                this.onChangeEntity({
                                    signing_date: entity.cancel_signing_date,
                                    cancel_signing_date: null,
                                    unsubscribed: null,
                                })
                            }>
                            Restore Premium
                        </Button>
                    )}
                    {(entity.signing_date || entity.cancel_signing_date) && (
                        <AutoRow>
                            <HoverText
                                enableClick
                                trigger={
                                    <PreIcon name="contact_mail">
                                        {format(
                                            getDate(
                                                entity.signing_date || entity.cancel_signing_date,
                                            ),
                                            'yyyy-MM-dd',
                                        )}
                                    </PreIcon>
                                }>
                                Client has signed the agreement
                            </HoverText>
                            <button
                                onClick={() =>
                                    entity.signing_date
                                        ? NiceModal.show(PremiumCancelModal, {
                                              providerId: entity.service_provider_id,
                                          })
                                        : unsubscribe(
                                              entity.service_provider_id,
                                              CANCEL_MODE_SILENT,
                                              null,
                                          )
                                }>
                                <Icon name="clear" />
                            </button>
                        </AutoRow>
                    )}
                </AutoRow>
                <Divider m />
                <AutoRow>
                    {entity.signing_date && (
                        <HoverText
                            trigger={
                                <AutoRow>
                                    <span
                                        style={
                                            phoneRoutingNrRequested
                                                ? {
                                                      filter: 'hue-rotate(270deg) saturate(80%) brightness(120%)',
                                                  }
                                                : {}
                                        }>
                                        {this.renderInput('charge_phone_calls')}
                                    </span>
                                    {hasFreeWebsiteClicks ? (
                                        <b style={{ color: '#ed7d31' }}>
                                            Free website clicks (link exchange)
                                        </b>
                                    ) : (
                                        this.renderInput('charge_website_clicks')
                                    )}
                                    <span
                                        style={
                                            canToggleReclaiming
                                                ? undefined
                                                : {
                                                      pointerEvents: 'none',
                                                      opacity: 0.5,
                                                  }
                                        }>
                                        {this.renderInput('is_reclaiming_enabled')}
                                    </span>
                                    {this.renderInput('extended_match_details')}
                                </AutoRow>
                            }>
                            {phoneRoutingNrRequested && (
                                <>
                                    Customer requested phone leads but profile status is not enabled
                                    yet
                                </>
                            )}
                        </HoverText>
                    )}
                    {(_isTouched || _saving) && (
                        <Button onClick={onSave} disabled={!_isTouched}>
                            {_saving ? 'saving..' : 'Save'}
                        </Button>
                    )}
                </AutoRow>
                {(provider.active_request_sending_apis || []).map((api: Api) => (
                    <Fragment key={api.api_id}>
                        <Divider m />
                        <Row middle="xs">
                            <Col x>
                                <span style={{ color: '#2ece40', fontWeight: 700 }}>
                                    API active: {api.display_name}
                                </span>
                            </Col>
                            {Number(api.api_id) === PARTNER_PUSH_API_ID ? (
                                <Col x>
                                    Lead mail is set per subscription on the Partner push page
                                </Col>
                            ) : (
                                <>
                                    <Col x>Leads via mail:</Col>
                                    <Col x>
                                        <Toggle
                                            name="always_mail_lead"
                                            value={api.always_mail_lead}
                                            textTrue="True"
                                            textFalse="False"
                                            onChange={() => {
                                                this.onChangeOtherEntity({
                                                    store: 'providers',
                                                    name: 'apis',
                                                    key: api.api_id,
                                                    diff: {
                                                        always_mail_lead: !api.always_mail_lead,
                                                    },
                                                })
                                                saveApi(api.api_id)
                                            }}
                                        />
                                    </Col>
                                </>
                            )}
                        </Row>
                    </Fragment>
                ))}
                {logArr.length > 0 && <Divider m />}
                {logArr.map((log: Log, i: number) => (
                    <AutoRow key={log.log_id}>
                        {format(getDate(log.created), 'dd/MM/yyyy')}
                        {logArr[i + 1]
                            ? ' - ' + format(getDate(logArr[i + 1].created), 'dd/MM/yyyy')
                            : ''}
                        {log.log_type === 'subscribe' ? (
                            <span style={{ color: '#0CD605' }}>Active</span>
                        ) : (
                            <span style={{ color: '#FF0000' }}>Unsubscribed</span>
                        )}
                    </AutoRow>
                ))}
            </div>
        )
    }
}

const ConnectedLeadSettings = EntityComponentContainer(LeadSettings, {
    store: 'providers',
    name: 'leadSettings',
    saveFunc: saveLeadSetting,
    mapDispatchToProps: (dispatch: any) => ({
        saveApi: (apiId: string) => dispatch(saveApi(apiId)),
        fetchProvider: (providerId: number, forceFetch: boolean) =>
            dispatch(fetchProvider(providerId, forceFetch)),
        unsubscribe: (providerId: number, mode: string, reason: string | null) =>
            dispatch(unsubscribe(providerId, mode, reason)),
    }),
})

interface WrapperProps {
    providerId: number
    [prop: string]: unknown
}

/**
 * The replacement leads are not part of the normalised provider payload, so they are fetched once
 * here and handed down, rather than once per badge.
 */
const LeadSettingsWithReplacementLeads = ({ providerId, ...props }: WrapperProps) => {
    const { stats } = useReplacementLeadStats(providerId)

    return <ConnectedLeadSettings {...props} replacementLeadStats={stats} />
}

export default LeadSettingsWithReplacementLeads
